// src/screens/LandingScreen.js
// ============================================================================
// LANDING / SPLASH SCREEN
//
// First impression for anyone hitting https://critcash.com. Auto-redirects
// signed-in users to Lobby; signed-out users see the full landing page with
// value prop, how-it-works, featured game, Founders 128 hook, and primary CTA.
//
// Pre-2026-05-30 this was a logo + login button on black — borderline
// maintenance-page UX. Rebuilt before launch so first-time visitors actually
// understand what CritCash is in the first 5 seconds.
//
// 2026-10-12 (founder pick, Option B of CritCash/SPLASH_CRITCAST_OPTIONS_
// 2026-10-12.md): leads with CritCast, "the streaming platform with the tools
// built in". Every claim was checked against what is live today (squad starts
// and simulcast say paid plan; Verified results say "supported games"). Money,
// Crits and seasons stay off this page. Keep claims true when editing.
// ============================================================================

import React, { useEffect, useState } from 'react';
import {
  View,
  Image,
  TouchableOpacity,
  Text,
  StyleSheet,
  ActivityIndicator,
  ScrollView,
  Platform,
  useWindowDimensions,
} from 'react-native';
import { Ionicons, MaterialCommunityIcons, FontAwesome5 } from '@expo/vector-icons';
import { onAuthStateChanged } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth } from '../../firebaseConfig';
import Footer from '../components/Footer';
import CyberpunkBackground from '../components/CyberpunkBackground';
import FounderBadge from '../components/FounderBadge';

// Brand colors — matches the rest of the app
const COLORS = {
  BG: '#000',
  CARD: '#0d0d0d',
  CARD_BORDER: '#1a1a1a',
  GOLD: '#FCD000',
  CYAN: '#3aafff',
  MAGENTA: '#ff2eb0',          // eSport accent for variety against gold/cyan
  TEXT: '#ffffff',
  BODY: '#c9cdd8',             // section body copy: a step brighter than DIM
  DIM: '#9aa0b4',
  SUCCESS: '#2ECC71',
  // Brand-tinted backgrounds — barely-perceptible hues that warm up the
  // pure-black aesthetic and lean into the eSport feel without becoming
  // distracting. Each section picks one to break up the scroll.
  TINT_GOLD: '#0f0c00',        // very dim gold-warm dark
  TINT_CYAN: '#06121f',        // very dim cyan-cool dark
  TINT_MAGENTA: '#13030d',     // very dim magenta-warm dark
};

// Web-only blur for the radial glows. On native we just render the colored
// circle at low opacity — readable but less polished. The whole site is web-
// first today so this is the right tradeoff.
const blurStyle = (px) => (Platform.OS === 'web' ? { filter: `blur(${px}px)` } : {});

// Web-only CSS background helper — passes a raw `background` shorthand
// through React Native Web so we can use real CSS gradients without
// expo-linear-gradient. Falls back to a plain backgroundColor on native.
const webBg = (cssValue, fallbackColor) =>
  Platform.OS === 'web' ? { background: cssValue } : { backgroundColor: fallbackColor };

// Frosted-glass card treatment — translucent background + backdrop blur.
// Web-only since RN doesn't expose backdrop-filter natively.
const glassStyle = (alpha = 0.04) =>
  Platform.OS === 'web'
    ? {
        backgroundColor: `rgba(255, 255, 255, ${alpha})`,
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
      }
    : { backgroundColor: '#0d0d0d' };

export default function LandingScreen({ navigation }) {
  const [initializing, setInitializing] = useState(true);
  // Live Founder-badge scarcity for the FOMO section. Public count endpoint
  // (getFounderBadgeStatus) — no auth needed since the landing page is
  // signed-out. Null until loaded; the section falls back to the cap.
  const [foundersLeft, setFoundersLeft] = useState(null);
  const { width } = useWindowDimensions();
  // Desktop layout kicks in past ~900px — content stays centered with a
  // capped max width to avoid blown-out line lengths on wide monitors.
  const isDesktop = width >= 900;

  useEffect(() => {
    const subscriber = onAuthStateChanged(auth, (user) => {
      if (user) {
        navigation.replace('Home');
      } else {
        setInitializing(false);
      }
    });
    return subscriber;
  }, [navigation]);

  useEffect(() => {
    httpsCallable(getFunctions(), 'getFounderBadgeStatus')()
      .then((res) => setFoundersLeft(res.data))
      .catch(() => { /* non-critical — section falls back to the cap */ });
  }, []);

  if (initializing) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={COLORS.GOLD} />
      </View>
    );
  }

  return (
    <View style={styles.outerContainer}>
      {/* Animated cyberpunk backdrop — drifting brand-color mesh, scrolling
          perspective grid, floating particles, scanlines, vignette. Lives
          behind every section so the page reads as a single premium scene
          with constant subliminal motion. Web-only; native falls back to
          flat black inside the component itself. */}
      <CyberpunkBackground />
      <ScrollView
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        {/* ============================================================== */}
        {/* HERO                                                            */}
        {/* ============================================================== */}
        <View style={[styles.heroWrap, isDesktop && styles.heroWrapDesktop]}>
          {/* Muted gameplay reel looping behind the hero copy — sits under
              the scrim, glows, and content so the headline and CTAs keep
              full contrast. Web-only; native keeps the flat black hero. */}
          <HeroVideoBackdrop />

          {/* Ambient brand glows — three blurred circles positioned around
              the hero to give the section a sense of energy without a real
              illustration. Gold top-left, cyan bottom-right, magenta accent
              just-off-center. On native these render as soft colored circles;
              on web the blur filter turns them into ambient light. */}
          <View pointerEvents="none" style={[styles.glowGold, blurStyle(90)]} />
          <View pointerEvents="none" style={[styles.glowCyan, blurStyle(90)]} />
          <View pointerEvents="none" style={[styles.glowMagenta, blurStyle(120)]} />

          <View style={[styles.hero, isDesktop && styles.heroDesktop]}>
            <Image
              source={require('../../assets/critcast_logo_transparent.webp')}
              style={styles.logoWide}
              resizeMode="contain"
              accessibilityLabel="CritCast"
            />
            <Text style={styles.eyebrow}>
              STREAM ANY GAME · FREE TO START · FREE TO WATCH
            </Text>
            <Text style={[styles.heroTitle, isDesktop && styles.heroTitleDesktop]}>
              Go live.{'\n'}
              <Text style={{ color: COLORS.GOLD }}>The rest is built in.</Text>
            </Text>
            <Text style={[styles.heroSubtitle, isDesktop && styles.heroSubtitleDesktop]}>
              Run your own events and leagues, and save your streams as videos,
              all in one place. Your viewers watch free, no account needed.
            </Text>

            <View style={[styles.ctaRow, isDesktop && styles.ctaRowDesktop]}>
              <TouchableOpacity
                style={styles.primaryCta}
                onPress={() => navigation.navigate('SignUp')}
              >
                <Text style={styles.primaryCtaText}>Start Streaming</Text>
                <Ionicons name="arrow-forward" size={16} color="#000" style={{ marginLeft: 8 }} />
              </TouchableOpacity>
              {/* Watch is a PRIMARY path, not an afterthought (founder
                  2026-08-12): most first-time visitors arrive to watch, not
                  to broadcast. Sign In demotes to the ghost row below. */}
              <TouchableOpacity
                style={styles.secondaryCta}
                onPress={() => navigation.navigate('Streams')}
              >
                <Text style={styles.secondaryCtaText}>Watch Live</Text>
              </TouchableOpacity>
            </View>

            <View style={styles.ghostLinkRow}>
              <TouchableOpacity
                onPress={() => navigation.navigate('HowToPlay')}
                style={styles.ghostLink}
              >
                <Text style={styles.ghostLinkText}>How it works ↓</Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={() => navigation.navigate('Login')}
                style={styles.ghostLink}
              >
                <Text style={styles.ghostLinkText}>Sign in →</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>

        {/* Branded gold divider — single horizontal accent line that
            breaks the harsh dark-on-dark transition between the hero
            and the trust strip below. Gradient-faded ends keep it from
            forming hard left/right edges; subtle gold glow on web. */}
        <View pointerEvents="none" style={styles.sectionDivider} />

        {/* ============================================================== */}
        {/* TRUST STRIP                                                     */}
        {/* ============================================================== */}
        <View style={[styles.trustStrip, isDesktop && styles.trustStripDesktop]}>
          <TrustItem
            icon={<Ionicons name="gift-outline" size={20} color={COLORS.GOLD} />}
            label="Free to Start"
            sub="Go live on a free account. No card required."
          />
          <TrustItem
            icon={<Ionicons name="eye-outline" size={20} color={COLORS.GOLD} />}
            label="Free to Watch"
            sub="Streams, events and videos. No account needed."
          />
          <TrustItem
            icon={<MaterialCommunityIcons name="trophy-variant" size={20} color={COLORS.GOLD} />}
            label="Your Events"
            sub="Brackets, head-to-heads and leagues, set up by you."
          />
          <TrustItem
            icon={<MaterialCommunityIcons name="shield-check-outline" size={20} color={COLORS.GOLD} />}
            label="Verified Results"
            sub="Straight from your stream, on supported games."
          />
        </View>

        {/* ============================================================== */}
        {/* YOUR CHANNEL — the streamer track                               */}
        {/* ============================================================== */}
        {/* Bands carry the section tints full-bleed and stay translucent so
            the falling dice behind the page keep showing through. Video
            retention is 7 days free (settings/videos); publishing and
            simulcast are paid-plan perks, so the copy says so. */}
        <View style={[styles.band, styles.bandCyan]}>
          <View style={[styles.section, isDesktop && styles.sectionDesktop]}>
            <Text style={[styles.sectionEyebrow, styles.sectionEyebrowGold]}>YOUR CHANNEL</Text>
            <Text style={[styles.sectionTitle, isDesktop && styles.sectionTitleDesktop]}>Set it up once.</Text>

            <Text style={[styles.critsBody, isDesktop && styles.critsBodyDesktop]}>
              One stream key works for every game you play. Post your weekly
              schedule and your channel shows viewers when you're on next. Every
              stream saves as a video, kept 7 days on a free account.
            </Text>
            <Text style={[styles.critsBody, isDesktop && styles.critsBodyDesktop]}>
              On a paid plan, publish your best videos to your profile and send
              your stream to the other places your audience already watches, at
              the same time.
            </Text>

            <TouchableOpacity
              style={[styles.primaryCta, { marginTop: 28, flex: 0, alignSelf: 'center', paddingHorizontal: 32 }]}
              onPress={() => navigation.navigate('Streams')}
            >
              <Text style={styles.primaryCtaText}>Browse Channels</Text>
              <Ionicons name="arrow-forward" size={16} color="#000" style={{ marginLeft: 8 }} />
            </TouchableOpacity>
          </View>
        </View>

        {/* ============================================================== */}
        {/* FOR VIEWERS — the viewer track                                  */}
        {/* ============================================================== */}
        {/* Founder 2026-08-12: most first-time visitors arrive to watch.
            Watching is open logged-out BY DESIGN (see
            project_public_spectator_viewing); chat and follow need a free
            account. The squad page shows up to 4 tiles on desktop and 2 on
            phones, so the copy says "side by side", never "every angle". */}
        <View style={[styles.section, isDesktop && styles.sectionDesktop]}>
          <Text style={[styles.sectionEyebrow, styles.sectionEyebrowGold]}>FOR VIEWERS</Text>
          <Text style={[styles.sectionTitle, isDesktop && styles.sectionTitleDesktop]}>Pull up a seat.</Text>

          <Text style={[styles.critsBody, isDesktop && styles.critsBodyDesktop]}>
            Browse who's live by game. When a squad streams together, watch them
            side by side. Tap a player to swap them in, and the sound follows
            whoever you choose.
          </Text>
          <Text style={[styles.critsBody, isDesktop && styles.critsBodyDesktop]}>
            When it's an event, you're watching a result go on the record. Make a
            free account to chat and follow, and you'll hear the moment your
            streamers go live.
          </Text>

          <TouchableOpacity
            style={[styles.primaryCta, { marginTop: 28, flex: 0, alignSelf: 'center', paddingHorizontal: 32 }]}
            onPress={() => navigation.navigate('Streams')}
          >
            <Text style={styles.primaryCtaText}>Watch What's Live</Text>
            <Ionicons name="arrow-forward" size={16} color="#000" style={{ marginLeft: 8 }} />
          </TouchableOpacity>
        </View>

        {/* ============================================================== */}
        {/* HOW IT WORKS                                                    */}
        {/* ============================================================== */}
        <View style={[styles.band, styles.bandGold]}>
          <View style={[styles.section, isDesktop && styles.sectionDesktop]}>
            <Text style={[styles.sectionEyebrow, styles.sectionEyebrowGold]}>HOW IT WORKS</Text>
            <Text style={[styles.sectionTitle, isDesktop && styles.sectionTitleDesktop]}>From sign-up to your own event</Text>

            <View style={[styles.steps, isDesktop && styles.stepsDesktop]}>
              <StepCard
                num={1}
                title="Sign Up Free"
                body="Make your account and claim your channel."
              />
              <StepCard
                num={2}
                title="Go Live"
                body="Drop your stream key into OBS or Streamlabs. On console, use the free Remote Play app on your PC or Mac, or a capture card."
              />
              <StepCard
                num={3}
                title="Bring Your Squad"
                body="Your whole crew on one page, with one chat instead of five. Start a squad stream on any paid plan; your teammates join free."
              />
              <StepCard
                num={4}
                title="Run Your Own"
                body="Set up a bracket, a head-to-head or a league in a few taps, and play it on stream."
              />
            </View>
          </View>
        </View>

        {/* ============================================================== */}
        {/* THE LINEUP                                                      */}
        {/* ============================================================== */}
        {/* No game names until config/gameAvailability is checked: Verified
            results run only on supported games, the rest are player-reported
            with disputes reviewed, and racing games in the catalog run
            Showdowns instead of brackets. */}
        <View style={[styles.featuredGame, isDesktop && styles.featuredGameDesktop]}>
          <Text style={styles.sectionEyebrow}>THE LINEUP</Text>
          <Text style={[styles.featuredTitle, isDesktop && styles.featuredTitleDesktop]}>Bring any game.</Text>
          <Text style={[styles.featuredBody, isDesktop && styles.critsBodyDesktop]}>
            Run a head-to-head or a league on whatever you play, plus brackets
            or full-grid Showdowns, depending on the game. Supported games get
            Verified results; on the rest, players report the result and
            disputes get reviewed.
          </Text>
          <View style={styles.featuredPills}>
            <View style={styles.pill}><Text style={styles.pillText}>Racing</Text></View>
            <View style={styles.pill}><Text style={styles.pillText}>Sports</Text></View>
            <View style={styles.pill}><Text style={styles.pillText}>FPS</Text></View>
            <View style={styles.pill}><Text style={styles.pillText}>Fighting</Text></View>
            <View style={styles.pill}><Text style={styles.pillText}>Any Game</Text></View>
          </View>
          <TouchableOpacity
            style={[styles.primaryCta, { marginTop: 28, flex: 0, alignSelf: 'center', paddingHorizontal: 32 }]}
            onPress={() => navigation.navigate('Lobby')}
          >
            <Text style={styles.primaryCtaText}>Browse Events</Text>
            <Ionicons name="arrow-forward" size={16} color="#000" style={{ marginLeft: 8 }} />
          </TouchableOpacity>
        </View>

        {/* ============================================================== */}
        {/* YOUR RECORD                                                     */}
        {/* ============================================================== */}
        {/* The arena idea, scoped to events: casual streams are never said
            to "count". Leaderboards list only ID- and age-verified players,
            so the copy says they "remember", not that everyone is on them. */}
        <View style={[styles.section, isDesktop && styles.sectionDesktop]}>
          <Text style={[styles.sectionEyebrow, styles.sectionEyebrowGold]}>YOUR RECORD</Text>
          <Text style={[styles.sectionTitle, isDesktop && styles.sectionTitleDesktop]}>Your viewers saw it. Your profile keeps it.</Text>

          <Text style={[styles.critsBody, isDesktop && styles.critsBodyDesktop]}>
            The clutch comeback, the perfect race, the night you couldn't lose.
            Usually they're gone the moment the lobby closes. Here, every event
            is free to enter and every match is played on stream.
          </Text>
          <Text style={[styles.critsBody, isDesktop && styles.critsBodyDesktop]}>
            League standings update as you play. Your wins and results by game
            stay on your profile, and the leaderboards remember.{' '}
            <Text style={{ color: COLORS.GOLD, fontWeight: '700' }}>
              From your first event here, it counts.
            </Text>
          </Text>

          <TouchableOpacity
            onPress={() => navigation.navigate('FAQ')}
            style={[styles.ghostLink, { alignSelf: 'center', marginTop: 18 }]}
          >
            <Text style={[styles.ghostLinkText, { color: COLORS.CYAN }]}>
              Read the full FAQ →
            </Text>
          </TouchableOpacity>
        </View>

        {/* ============================================================== */}
        {/* FOUNDERS 128 HOOK                                               */}
        {/* ============================================================== */}
        <View style={[styles.founders128, isDesktop && styles.founders128Desktop]}>
          <View style={[styles.foundersText, isDesktop && styles.foundersTextDesktop]}>
            <Text style={styles.foundersEyebrow}>FOUNDERS 128 COLLECTION</Text>
            <Text style={[styles.foundersTitle, isDesktop && styles.foundersTitleDesktop]}>First Blood Edition</Text>
            <Text style={styles.foundersBody}>
              The first 128 people with the CRITCASH-FOUNDER code can order the
              original Founder Tee. When they're gone, they're gone: no restock,
              no second run. If you have a code, you're early.
            </Text>
            <View style={styles.foundersCtaRow}>
              <TouchableOpacity
                style={styles.primaryCta}
                onPress={() => navigation.navigate('Store')}
              >
                <Text style={styles.primaryCtaText}>View the Store</Text>
              </TouchableOpacity>
            </View>
          </View>
          <View style={[styles.foundersImageWrap, isDesktop && styles.foundersImageWrapDesktop]}>
            {/* Branded hero card matching the FounderStoreScreen treatment:
                MerchBackground.webp behind the bare shirt + a gold-tinted
                bordered frame around it. Founder asked specifically to
                mirror what the Store renders so the landing reads as a
                premium product showcase instead of a floating-model
                photoshoot. Uses the solo shirt (no model) so there's
                nothing to ground — the backdrop IS the ground. */}
            <View style={styles.brandedHero}>
              <Image
                source={require('../../assets/MerchBackground.webp')}
                style={styles.brandedHeroBg}
                resizeMode="cover"
              />
              <Image
                source={require('../../assets/FounderTee_Solo.webp')}
                style={styles.brandedHeroFg}
                resizeMode="contain"
              />
            </View>
          </View>
        </View>

        {/* ============================================================== */}
        {/* FOUNDER BADGE — the digital scarcity collectible                */}
        {/* ============================================================== */}
        {/* Twin to the Founders 128 tee above: a second limited Founder
            drop. The tee is physical + gold; the badge is digital +
            electric-blue, so this section is cyan-tinted. Renders the real
            FounderBadge aura so visitors see exactly what they'd earn. The
            stand-in avatar is the King Crit skull alone, without the old
            wordmark, since the page leads with CritCast. */}
        <View style={[styles.founderBadgeSection, isDesktop && styles.founderBadgeSectionDesktop]}>
          <View style={[styles.badgeShowcase, isDesktop && styles.badgeShowcaseDesktop]}>
            <FounderBadge size={132} active>
              <View style={styles.badgeAvatar}>
                <Image
                  source={require('../../assets/logoIcon4.png')}
                  style={styles.badgeAvatarLogo}
                  resizeMode="contain"
                />
              </View>
            </FounderBadge>
            <View style={styles.badgeChip}>
              <Text style={styles.badgeChipText}>FOUNDER</Text>
            </View>
          </View>

          <View style={[styles.foundersText, isDesktop && styles.foundersTextDesktop]}>
            <Text style={styles.badgeEyebrow}>THE FOUNDER BADGE</Text>
            <Text style={[styles.foundersTitle, isDesktop && styles.foundersTitleDesktop]}>Be one of the first 1,000.</Text>
            <Text style={styles.foundersBody}>
              The Founder badge marks the people who got here first. The gold
              King Crit die orbits your avatar with an electric-blue glow on your
              profile, the leaderboard and every event you enter, and your videos
              carry the Founder tag. Go yearly on any paid plan, or monthly on Pro
              or Elite, to claim one. Only 1,000 will ever exist, and yours is
              yours for life.
            </Text>

            <View style={styles.scarcityRow}>
              <Text style={styles.scarcityNum}>
                {foundersLeft ? Number(foundersLeft.remaining).toLocaleString() : '1,000'}
              </Text>
              <Text style={styles.scarcityLabel}>
                of {foundersLeft ? Number(foundersLeft.cap).toLocaleString() : '1,000'}{'\n'}badges left
              </Text>
            </View>

            <View style={styles.foundersCtaRow}>
              <TouchableOpacity
                style={styles.primaryCta}
                onPress={() => navigation.navigate('SignUp')}
              >
                <Text style={styles.primaryCtaText}>Become a Founder</Text>
                <Ionicons name="arrow-forward" size={16} color="#000" style={{ marginLeft: 8 }} />
              </TouchableOpacity>
            </View>
            <Text style={styles.badgeFootnote}>Free to join. The badge comes with a qualifying paid plan.</Text>
          </View>
        </View>

        {/* ============================================================== */}
        {/* FINAL CTA                                                       */}
        {/* ============================================================== */}
        <View style={[styles.finalCta, isDesktop && styles.finalCtaDesktop]}>
          <Text style={[styles.finalTitle, isDesktop && styles.finalTitleDesktop]}>Go live tonight.</Text>
          <Text style={[styles.finalSubtitle, isDesktop && styles.critsBodyDesktop]}>
            Sign up takes a minute. Just here to watch? You don't need an account.
          </Text>
          <View style={[styles.ctaRow, isDesktop && styles.ctaRowDesktop, { marginTop: 24 }]}>
            <TouchableOpacity
              style={styles.primaryCta}
              onPress={() => navigation.navigate('SignUp')}
            >
              <Text style={styles.primaryCtaText}>Start Streaming</Text>
              <Ionicons name="arrow-forward" size={16} color="#000" style={{ marginLeft: 8 }} />
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.secondaryCta}
              onPress={() => navigation.navigate('Streams')}
            >
              <Text style={styles.secondaryCtaText}>Watch Live</Text>
            </TouchableOpacity>
          </View>
          <Text style={styles.finalDisclaimer}>
            Free to start. No purchase required to create an account or enter
            events. You must be 18 or older. Open worldwide; prices shown in USD.
            Built and operated by CritCash, Inc.
          </Text>
        </View>

        <Footer navigation={navigation} />
      </ScrollView>
    </View>
  );
}

// ----------------------------------------------------------------------------
// Sub-components
// ----------------------------------------------------------------------------

// Muted, looping multi-game gameplay montage behind the hero copy. Web-only —
// rendered as a raw DOM <video> (same raw-DOM pattern as CyberpunkBackground)
// because RN Web has no video primitive and its style validator strips
// CSS-only props like objectFit. The file lives in web/media/ and ships as a
// static hosting asset instead of going through the webpack bundle. Users
// with prefers-reduced-motion get the still poster frame instead.
const HERO_VIDEO_SRC = '/media/hero-bg.mp4';
const HERO_VIDEO_POSTER = '/media/hero-bg-poster.jpg';

const heroVideoStyle = {
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
};

// Dark scrim over the reel — heavier at the top (logo/eyebrow) and bottom
// (transition into the gold divider), lighter mid-frame so the gameplay
// still reads through behind the headline.
const heroScrimStyle = {
  position: 'absolute',
  inset: 0,
  background:
    'linear-gradient(to bottom, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.55) 45%, rgba(0,0,0,0.85) 100%)',
};

function HeroVideoBackdrop() {
  if (Platform.OS !== 'web') return null;

  const reducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  return (
    <View pointerEvents="none" style={styles.heroVideoWrap}>
      {reducedMotion ? (
        <img src={HERO_VIDEO_POSTER} alt="" aria-hidden="true" style={heroVideoStyle} />
      ) : (
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster={HERO_VIDEO_POSTER}
          aria-hidden="true"
          // React doesn't reliably reflect `muted` onto the DOM attribute,
          // and mobile browsers refuse to autoplay unmuted video — set the
          // property directly and kick playback ourselves.
          ref={(el) => {
            if (el) {
              el.muted = true;
              const p = el.play();
              if (p && p.catch) p.catch(() => {});
            }
          }}
          style={heroVideoStyle}
        >
          <source src={HERO_VIDEO_SRC} type="video/mp4" />
        </video>
      )}
      <div style={heroScrimStyle} />
    </View>
  );
}

const TrustItem = ({ icon, label, sub }) => (
  <View style={styles.trustItem}>
    <View style={styles.trustIconWrap}>{icon}</View>
    <Text style={styles.trustLabel}>{label}</Text>
    <Text style={styles.trustSub}>{sub}</Text>
  </View>
);

const StepCard = ({ num, title, body }) => (
  <View style={styles.stepCard}>
    <View style={styles.stepNumberWrap}>
      <Text style={styles.stepNumber}>{num}</Text>
    </View>
    <Text style={styles.stepTitle}>{title}</Text>
    <Text style={styles.stepBody}>{body}</Text>
  </View>
);

// ----------------------------------------------------------------------------
// Styles
// ----------------------------------------------------------------------------

const styles = StyleSheet.create({
  outerContainer: {
    flex: 1,
    backgroundColor: COLORS.BG,
    // Required so the absolute-positioned atmosphericBg below has a
    // positioning context relative to the screen, not the viewport.
    position: 'relative',
  },
  scrollContent: {
    flexGrow: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: COLORS.BG,
  },

  // ============================================================
  // HERO
  // ============================================================
  // Outer wrapper holds the absolute-positioned ambient glows + the
  // content. Overflow hidden so the glows can't bleed past the section
  // boundary and into the trust strip below.
  heroWrap: {
    position: 'relative',
    overflow: 'hidden',
    backgroundColor: COLORS.BG,
  },
  heroWrapDesktop: {
    // No layout change at desktop — the inner .hero handles centering.
  },
  // Full-bleed container for the hero gameplay reel + scrim. Same z-plane
  // as the ambient glows but rendered first, so the glows and content
  // stack above it. pointerEvents none is set on the component.
  heroVideoWrap: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 0,
    overflow: 'hidden',
  },
  hero: {
    alignItems: 'center',
    paddingTop: 64,
    paddingBottom: 56,
    paddingHorizontal: 20,
    // Important: position relative so the content stays above the
    // absolutely-positioned glow circles in z-order.
    position: 'relative',
    zIndex: 1,
  },
  heroDesktop: {
    paddingTop: 90,
    paddingBottom: 90,
    maxWidth: 900,
    alignSelf: 'center',
    width: '100%',
  },
  logoWide: {
    // CritCast badge wordmark, 900×336 source (2.68:1).
    width: 300,
    height: 112,
    marginBottom: 24,
  },

  // ============================================================
  // AMBIENT GLOWS
  // ============================================================
  // Three large colored circles positioned around the hero. On web the
  // CSS blur filter (applied via blurStyle helper) softens them into
  // ambient light. On native they show as low-opacity colored circles —
  // less polished but readable. Combined with the brand-tinted section
  // backgrounds further down they give the page an eSport "energy" feel
  // without requiring real illustration assets.
  glowGold: {
    position: 'absolute',
    top: -120,
    left: -100,
    width: 420,
    height: 420,
    borderRadius: 210,
    backgroundColor: COLORS.GOLD,
    opacity: 0.18,
    zIndex: 0,
  },
  glowCyan: {
    position: 'absolute',
    bottom: -120,
    right: -100,
    width: 460,
    height: 460,
    borderRadius: 230,
    backgroundColor: COLORS.CYAN,
    opacity: 0.16,
    zIndex: 0,
  },
  glowMagenta: {
    position: 'absolute',
    top: '40%',
    left: '50%',
    marginLeft: -180,
    width: 360,
    height: 360,
    borderRadius: 180,
    backgroundColor: COLORS.MAGENTA,
    opacity: 0.08,
    zIndex: 0,
  },

  // ============================================================
  // SECTION DIVIDER
  // ============================================================
  // Single 2px gold accent line with gradient-faded ends — sits
  // between the hero and the trust strip to soften the dark-on-
  // dark section transition with a branded color hit. Web uses a
  // linear-gradient + boxShadow for the soft fade + glow; native
  // falls back to a flat gold bar.
  sectionDivider: {
    width: '100%',
    height: 2,
    position: 'relative',
    zIndex: 2,
    ...(Platform.OS === 'web'
      ? {
          // RN Web's style validator rejects the `background` shorthand
          // and silently drops the value — must use long-form
          // backgroundImage for the gradient to actually render.
          backgroundImage:
            'linear-gradient(to right, transparent 0%, rgba(252, 208, 0, 0.85) 20%, #FCD000 50%, rgba(252, 208, 0, 0.85) 80%, transparent 100%)',
          boxShadow: '0 0 12px rgba(252, 208, 0, 0.55), 0 0 30px rgba(252, 208, 0, 0.30)',
        }
      : { backgroundColor: COLORS.GOLD }),
  },

  eyebrow: {
    color: COLORS.CYAN,
    fontSize: 11,
    fontWeight: '800',
    letterSpacing: 2,
    lineHeight: 18,
    textAlign: 'center',
    marginBottom: 16,
  },
  heroTitle: {
    color: COLORS.TEXT,
    fontSize: 34,
    fontWeight: '900',
    lineHeight: 40,
    textAlign: 'center',
    marginBottom: 18,
    letterSpacing: -0.5,
  },
  heroTitleDesktop: {
    fontSize: 56,
    lineHeight: 64,
    letterSpacing: -1,
  },
  heroSubtitle: {
    color: COLORS.BODY,
    fontSize: 15,
    lineHeight: 22,
    textAlign: 'center',
    maxWidth: 540,
    marginBottom: 32,
  },
  heroSubtitleDesktop: {
    fontSize: 17,
    lineHeight: 26,
  },
  ctaRow: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 10,
    width: '100%',
    maxWidth: 320,
  },
  ctaRowDesktop: {
    flexDirection: 'row',
    maxWidth: 460,
    gap: 14,
  },
  primaryCta: {
    backgroundColor: COLORS.GOLD,
    paddingVertical: 14,
    paddingHorizontal: 26,
    borderRadius: 10,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    flex: Platform.OS === 'web' ? 1 : 0,
    // Soft gold glow gives the primary CTA an "active" eSport feel without
    // being garish. Box-shadow on web; native gets nothing-equivalent.
    ...(Platform.OS === 'web' ? { boxShadow: '0 0 24px rgba(252, 208, 0, 0.35)' } : {}),
  },
  primaryCtaText: {
    color: '#000',
    fontWeight: '800',
    fontSize: 15,
  },
  secondaryCta: {
    backgroundColor: 'transparent',
    paddingVertical: 14,
    paddingHorizontal: 26,
    borderRadius: 10,
    borderWidth: 1.5,
    borderColor: COLORS.CYAN,
    alignItems: 'center',
    justifyContent: 'center',
    flex: Platform.OS === 'web' ? 1 : 0,
  },
  secondaryCtaText: {
    color: COLORS.CYAN,
    fontWeight: '700',
    fontSize: 15,
  },
  ghostLinkRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 22,
    justifyContent: 'center',
    marginTop: 14,
  },
  ghostLink: {
    marginTop: 8,
    paddingVertical: 8,
  },
  ghostLinkText: {
    color: COLORS.DIM,
    fontSize: 13,
  },
  critsBody: {
    color: COLORS.BODY,
    fontSize: 15,
    lineHeight: 24,
    textAlign: 'center',
    maxWidth: 620,
    alignSelf: 'center',
    marginBottom: 14,
  },
  critsBodyDesktop: {
    fontSize: 17,
    lineHeight: 27,
    maxWidth: 680,
  },

  // ============================================================
  // BANDS — full-bleed section tints
  // ============================================================
  // Translucent on purpose: the CyberpunkBackground (falling King Crit dice)
  // must keep showing through every section.
  band: {
    width: '100%',
    position: 'relative',
    zIndex: 1,
  },
  bandCyan: {
    ...webBg(
      'linear-gradient(to bottom, transparent 0%, rgba(6, 18, 31, 0.78) 18%, rgba(6, 22, 38, 0.88) 50%, rgba(6, 18, 31, 0.78) 82%, transparent 100%)',
      COLORS.TINT_CYAN
    ),
  },
  bandGold: {
    ...webBg(
      'linear-gradient(to bottom, transparent 0%, rgba(20, 16, 0, 0.78) 18%, rgba(28, 22, 0, 0.88) 50%, rgba(15, 12, 0, 0.78) 82%, transparent 100%)',
      COLORS.TINT_GOLD
    ),
  },

  // ============================================================
  // TRUST STRIP
  // ============================================================
  trustStrip: {
    flexDirection: 'column',
    // Soft gradient transition top + bottom — no hard borders. Section
    // emerges from + dissolves back into the atmospheric background so
    // the page feels continuous instead of blocked-out. Cyan tint is
    // dialed down to a hint since the atmospheric bg already carries the
    // brand-color story.
    ...webBg(
      'linear-gradient(to bottom, transparent 0%, rgba(6, 18, 31, 0.6) 30%, rgba(6, 18, 31, 0.6) 70%, transparent 100%)',
      COLORS.TINT_CYAN
    ),
    paddingHorizontal: 20,
    paddingVertical: 36,
    gap: 18,
    // Relative so the section sits above the atmospheric background
    position: 'relative',
    zIndex: 1,
  },
  trustStripDesktop: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    maxWidth: 1100,
    alignSelf: 'center',
    width: '100%',
    paddingVertical: 36,
  },
  trustItem: {
    alignItems: 'center',
    flex: 1,
    paddingHorizontal: 8,
  },
  trustIconWrap: {
    marginBottom: 8,
  },
  trustLabel: {
    color: COLORS.TEXT,
    fontWeight: '700',
    fontSize: 14,
    marginBottom: 4,
  },
  trustSub: {
    color: COLORS.DIM,
    fontSize: 12,
    textAlign: 'center',
    lineHeight: 16,
  },

  // ============================================================
  // SECTION (generic)
  // ============================================================
  section: {
    paddingVertical: 80,
    paddingHorizontal: 20,
    // Sit above the atmospheric background gradient
    position: 'relative',
    zIndex: 1,
  },
  sectionDesktop: {
    maxWidth: 1100,
    alignSelf: 'center',
    width: '100%',
    paddingVertical: 100,
  },
  sectionEyebrow: {
    color: COLORS.CYAN,
    fontSize: 11,
    fontWeight: '800',
    letterSpacing: 2,
    textAlign: 'center',
    marginBottom: 10,
  },
  sectionEyebrowGold: {
    color: COLORS.GOLD,
  },
  sectionTitle: {
    color: COLORS.TEXT,
    fontSize: 28,
    lineHeight: 34,
    fontWeight: '900',
    textAlign: 'center',
    marginBottom: 28,
    letterSpacing: -0.5,
  },
  sectionTitleDesktop: {
    fontSize: 42,
    lineHeight: 48,
    letterSpacing: -1,
  },

  // ============================================================
  // STEPS
  // ============================================================
  steps: {
    flexDirection: 'column',
    gap: 16,
  },
  stepsDesktop: {
    flexDirection: 'row',
    gap: 16,
  },
  stepCard: {
    // Frosted-glass treatment via the helper — translucent rgba background
    // + backdrop-filter blur lets the atmospheric mesh background show
    // through subtly, giving the cards real depth instead of flat dark
    // rectangles. Hairline white border at the bottom edge sells the
    // glass illusion. Native falls back to a solid dark card.
    ...glassStyle(0.025),
    borderRadius: 16,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.06)',
    padding: 24,
    flex: 1,
    // Subtle depth shadow on web
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.04)' }
      : {}),
  },
  stepNumberWrap: {
    width: 36,
    height: 36,
    borderRadius: 10,
    backgroundColor: COLORS.GOLD,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 14,
    ...(Platform.OS === 'web' ? { boxShadow: '0 0 18px rgba(252, 208, 0, 0.4)' } : {}),
  },
  stepNumber: {
    color: '#000',
    fontWeight: '900',
    fontSize: 16,
  },
  stepTitle: {
    color: COLORS.TEXT,
    fontWeight: '800',
    fontSize: 16,
    marginBottom: 8,
  },
  stepBody: {
    color: COLORS.DIM,
    fontSize: 13,
    lineHeight: 19,
  },

  // ============================================================
  // FEATURED GAME
  // ============================================================
  // Cyan band with hairline edges (2026-10-12 Option B coloring), between
  // the gold How It Works band above and the gold Founders 128 below.
  featuredGame: {
    ...webBg(
      'linear-gradient(to bottom, rgba(6, 18, 31, 0.8) 0%, rgba(6, 22, 38, 0.9) 50%, rgba(6, 18, 31, 0.8) 100%)',
      COLORS.TINT_CYAN
    ),
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderColor: '#0f2a44',
    paddingVertical: 80,
    paddingHorizontal: 20,
    alignItems: 'center',
    position: 'relative',
    zIndex: 1,
  },
  featuredGameDesktop: {
    paddingVertical: 96,
  },
  featuredTitle: {
    color: COLORS.TEXT,
    fontSize: 32,
    lineHeight: 38,
    fontWeight: '900',
    marginBottom: 14,
    letterSpacing: -0.5,
    textAlign: 'center',
  },
  featuredTitleDesktop: {
    fontSize: 46,
    lineHeight: 52,
    letterSpacing: -1,
  },
  featuredBody: {
    color: COLORS.BODY,
    fontSize: 15,
    lineHeight: 23,
    textAlign: 'center',
    maxWidth: 620,
    marginBottom: 20,
  },
  featuredPills: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
    justifyContent: 'center',
  },
  pill: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: COLORS.CYAN,
    paddingVertical: 7,
    paddingHorizontal: 16,
    borderRadius: 999,
  },
  pillText: {
    color: COLORS.CYAN,
    fontSize: 13,
    fontWeight: '700',
  },

  // ============================================================
  // FOUNDERS 128
  // ============================================================
  // Gold tint here — the Founder Tee is the gold-branded scarcity moment,
  // tinting the section makes that connection visual without being garish.
  founders128: {
    // Soft gold gradient — fades up from the magenta section above + back
    // down toward the dark final CTA. The model gets its own spotlight
    // glow + floor shadow further below so it has visual anchoring.
    ...webBg(
      'linear-gradient(to bottom, transparent 0%, rgba(20, 16, 0, 0.75) 25%, rgba(28, 22, 0, 0.85) 50%, rgba(15, 12, 0, 0.75) 75%, transparent 100%)',
      COLORS.TINT_GOLD
    ),
    paddingVertical: 80,
    paddingHorizontal: 20,
    position: 'relative',
    zIndex: 1,
  },
  founders128Desktop: {
    flexDirection: 'row',
    alignItems: 'center',
    maxWidth: 1100,
    alignSelf: 'center',
    width: '100%',
    paddingVertical: 80,
    gap: 60,
  },
  foundersText: {},
  foundersTextDesktop: {
    flex: 1,
  },
  foundersEyebrow: {
    color: COLORS.GOLD,
    fontSize: 11,
    fontWeight: '800',
    letterSpacing: 2,
    marginBottom: 10,
  },
  foundersTitle: {
    color: COLORS.TEXT,
    fontSize: 28,
    fontWeight: '900',
    letterSpacing: -0.5,
    marginBottom: 14,
  },
  foundersTitleDesktop: {
    fontSize: 40,
    lineHeight: 46,
    letterSpacing: -1,
  },
  foundersBody: {
    color: COLORS.BODY,
    fontSize: 15,
    lineHeight: 23,
    marginBottom: 22,
  },
  foundersCtaRow: {
    flexDirection: 'row',
  },
  foundersImageWrap: {
    alignItems: 'center',
    marginTop: 30,
  },
  foundersImageWrapDesktop: {
    flex: 1,
    marginTop: 0,
  },
  // BrandedHero card — mirrors the FounderStoreScreen treatment so the
  // landing page Founder Tee section renders as a premium product showcase
  // identical to the Store. MerchBackground.webp is laid down first as the
  // backdrop; the bare shirt sits on top filling the same frame. Gold-
  // tinted hairline border + rounded corners + dark card fallback. Soft
  // outer shadow on web for depth.
  brandedHero: {
    position: 'relative',
    width: '100%',
    // Matches MerchBackground.webp native aspect (2735×2588 = 1.057).
    // With the frame matching the source, the full brand asset
    // (skull + CRITCASH wordmark + lightning) fits with no cropping.
    aspectRatio: 1.057,
    maxWidth: 520,
    alignSelf: 'center',
    borderRadius: 14,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: '#FCD00040',
    backgroundColor: '#0E0E0E',
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 24px 60px rgba(0, 0, 0, 0.55), 0 0 32px rgba(252, 208, 0, 0.15)' }
      : {}),
  },
  brandedHeroBg: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    height: '100%',
  },
  brandedHeroFg: {
    position: 'absolute',
    // Inset a touch from the frame edges so the shirt has visible
    // padding around it — lets the MerchBackground.webp brand artwork
    // breathe and show through instead of being mostly hidden behind
    // the shirt. Combined with resizeMode="contain" on the Image, the
    // shirt scales proportionally to fit and centers naturally.
    top: 16,
    left: 16,
    right: 16,
    bottom: 16,
  },

  // ============================================================
  // FOUNDER BADGE — digital collectible (twin to Founders 128)
  // ============================================================
  // Cyan tint to pair with the badge's electric-blue glow and set it
  // apart from the gold tee section directly above.
  founderBadgeSection: {
    ...webBg(
      'linear-gradient(to bottom, transparent 0%, rgba(6, 18, 31, 0.75) 25%, rgba(6, 26, 42, 0.9) 50%, rgba(6, 18, 31, 0.75) 75%, transparent 100%)',
      COLORS.TINT_CYAN
    ),
    paddingVertical: 80,
    paddingHorizontal: 20,
    position: 'relative',
    zIndex: 1,
  },
  founderBadgeSectionDesktop: {
    flexDirection: 'row',
    alignItems: 'center',
    maxWidth: 1100,
    alignSelf: 'center',
    width: '100%',
    paddingVertical: 80,
    gap: 60,
  },
  badgeShowcase: {
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 34,
  },
  badgeShowcaseDesktop: {
    flex: 1,
    marginBottom: 0,
  },
  badgeAvatar: {
    width: 132,
    height: 132,
    borderRadius: 66,
    backgroundColor: '#000',
    borderWidth: 2,
    borderColor: '#0e3746',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  badgeAvatarLogo: {
    // Sized so the full square logo (skull, crown, wordmark, lightning) fits
    // inside the circle without the rim clipping the wordmark. Its black
    // background blends into the black avatar, so the mark reads as floating
    // inside the electric-blue ring.
    width: 94,
    height: 94,
  },
  badgeChip: {
    marginTop: 18,
    borderWidth: 1,
    borderColor: 'rgba(0, 207, 255, 0.55)',
    backgroundColor: 'rgba(0, 207, 255, 0.10)',
    borderRadius: 999,
    paddingHorizontal: 14,
    paddingVertical: 5,
  },
  badgeChipText: {
    color: '#aff0ff',
    fontSize: 12,
    fontWeight: '800',
    letterSpacing: 1.5,
  },
  badgeEyebrow: {
    color: COLORS.CYAN,
    fontSize: 11,
    fontWeight: '800',
    letterSpacing: 2,
    marginBottom: 10,
  },
  scarcityRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 24,
  },
  scarcityNum: {
    color: COLORS.CYAN,
    fontSize: 44,
    fontWeight: '900',
    letterSpacing: -1,
    textShadowColor: 'rgba(0, 207, 255, 0.55)',
    textShadowOffset: { width: 0, height: 0 },
    textShadowRadius: 18,
  },
  scarcityLabel: {
    color: COLORS.DIM,
    fontSize: 13,
    fontWeight: '700',
    lineHeight: 17,
  },
  badgeFootnote: {
    color: COLORS.DIM,
    fontSize: 12.5,
    marginTop: 12,
  },

  // ============================================================
  // FINAL CTA
  // ============================================================
  finalCta: {
    // Soft fade from the founders section above into a darker
    // close-out for the page. No hard border — just a gradient floor
    // that lets the atmospheric mesh do its thing through the bottom.
    ...webBg(
      'linear-gradient(to bottom, transparent 0%, rgba(0, 0, 0, 0.7) 30%, rgba(0, 0, 0, 0.9) 100%)',
      '#080808'
    ),
    paddingVertical: 80,
    paddingHorizontal: 20,
    alignItems: 'center',
    position: 'relative',
    zIndex: 1,
  },
  finalCtaDesktop: {
    paddingVertical: 100,
  },
  finalTitle: {
    color: COLORS.TEXT,
    fontSize: 32,
    fontWeight: '900',
    letterSpacing: -0.5,
    marginBottom: 12,
    textAlign: 'center',
  },
  finalTitleDesktop: {
    fontSize: 46,
    lineHeight: 52,
    letterSpacing: -1,
  },
  finalSubtitle: {
    color: COLORS.BODY,
    fontSize: 15,
    lineHeight: 22,
    textAlign: 'center',
    maxWidth: 540,
  },
  finalDisclaimer: {
    color: '#555',
    fontSize: 11,
    lineHeight: 16,
    textAlign: 'center',
    marginTop: 28,
    maxWidth: 540,
  },
});
